import React from "react";
import { Link } from "react-router-dom";

// The record page's context column: a ruled panel of factual rows plus links
// to related pages. Sticky on large screens so it stays with the reader.
//
// Rows carry only facts that already exist elsewhere in the site (location,
// the canonical contact address) — this panel is structure, not a place to
// invent credentials.

export function RecordRow({ term, children }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[104px_1fr] sm:gap-4 border-b border-line py-4 last:border-b-0">
      <dt className="font-hanken font-bold uppercase text-[10.5px] tracking-[0.13em] text-steel/70 pt-1">
        {term}
      </dt>
      <dd className="font-hanken text-[15px] leading-relaxed text-ink">{children}</dd>
    </div>
  );
}

// `tone` keeps the panel readable against whichever band it sits on: paper on
// a white section, white on a paper section.
export default function ContextRail({ title, rows = [], linksTitle, links = [], tone = "onPaper" }) {
  const surface = tone === "onWhite" ? "bg-paper" : "bg-white";
  return (
    <aside className="lg:sticky lg:top-28 self-start">
      <div className={`${surface} border border-line rounded-sm p-6 sm:p-7`}>
        <h2 className="font-hanken font-bold uppercase text-[11px] tracking-[0.13em] text-navy">
          {title}
        </h2>
        <dl className="mt-3">
          {rows.map((row) => (
            <RecordRow key={row.term} term={row.term}>
              {row.href ? (
                <a
                  href={row.href}
                  target={row.external ? "_blank" : undefined}
                  rel={row.external ? "noreferrer" : undefined}
                  className="text-navy font-semibold hover:text-royal transition-colors duration-300 break-words"
                >
                  {row.value}
                </a>
              ) : (
                row.value
              )}
            </RecordRow>
          ))}
        </dl>

        {links.length > 0 && (
          <div className="mt-6 pt-5 border-t border-line">
            <h3 className="font-hanken font-bold uppercase text-[10.5px] tracking-[0.13em] text-steel/70">
              {linksTitle}
            </h3>
            <ul className="mt-3 space-y-2.5">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="group inline-flex items-center gap-2 font-hanken font-semibold text-[15px] text-ink hover:text-royal transition-colors duration-300"
                  >
                    {l.label}
                    <span
                      aria-hidden="true"
                      className="inline-block text-steel/60 transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:translate-x-1 group-hover:text-royal"
                    >
                      →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </aside>
  );
}
